import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatRelativeTime } from '../../../shared/utils/dateUtils';
import { getBlockchainName } from '../../../shared/utils/chainIdMapper';
import TransactionDetailView from './TransactionDetailView';
import { AVAILABLE_COLUMNS as TRANSACTION_COLUMNS } from '../config/transactionColumns';
import { TABLE_STYLES } from '../styles/dataTableStyles';

/** Resolve chain ID to display name; pass through if already a name. */
function resolveChainDisplayName(chain) {
  if (chain == null || chain === '') return '';
  const num = Number(chain);
  if (!Number.isNaN(num) && String(num) === String(chain).trim()) {
    return getBlockchainName(num) || String(chain);
  }
  return String(chain);
}

const TransactionRow = ({ transaction, isExpanded, onToggle, columns }) => {
  const styles = TABLE_STYLES;

  const getCellValue = (columnKey) => {
    if (columnKey === 'contract_name_chain') {
      const name = transaction.contract_name ?? transaction.contractName ?? '';
      const chainDisplay = resolveChainDisplayName(transaction.chain);
      const value = chainDisplay ? `${name || '—'} (${chainDisplay})` : (name || '—');
      return { type: 'text', value };
    }
    const def = TRANSACTION_COLUMNS[columnKey];
    const field = def?.field || columnKey;
    const raw = transaction[field] ?? transaction[field?.replace(/_/g, '')] ?? '';
    if (columnKey === 'transaction_time' && raw) {
      return { type: 'time', value: raw };
    }
    if (columnKey === 'value' && (raw || raw === 0)) {
      return { type: 'value', value: raw };
    }
    return { type: 'text', value: raw };
  };

  const getAlignmentClass = (columnKey) => {
    const isFirst = columnKey === columns[0];
    const isLast = columnKey === columns[columns.length - 1];
    if (isFirst) return 'text-left';
    if (isLast) return 'text-right';
    return 'text-center';
  };

  const renderCell = (columnKey) => {
    const cell = getCellValue(columnKey);
    const alignmentClass = getAlignmentClass(columnKey);

    if (cell && typeof cell === 'object' && cell.type === 'time' && cell.value) {
      const d = new Date(cell.value);
      return (
        <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
          {formatRelativeTime(d)}
        </div>
      );
    }
    if (cell && typeof cell === 'object' && cell.type === 'value') {
      return (
        <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
          {typeof cell.value === 'number' ? cell.value.toLocaleString() : String(cell.value || '')}
        </div>
      );
    }
    const val = cell && typeof cell === 'object' ? cell.value : cell;
    const display = columnKey === 'tx_hash' && val && String(val).length > 20
      ? `${String(val).substring(0, 10)}...${String(val).slice(-8)}`
      : (val ?? 'N/A');
    const isFirst = columnKey === columns[0];
    return (
      <div
        className={`text-sm ${isFirst ? 'font-medium' : ''}`}
        style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}
      >
        {display}
      </div>
    );
  };

  const displayColumns = columns && columns.length > 0 ? columns : ['contract_name_chain', 'function_name', 'transaction_time'];

  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer transition-colors"
        style={{
          borderBottom: isExpanded ? 'none' : `1px solid ${styles.borderColor}`,
          backgroundColor: 'transparent'
        }}
        onMouseEnter={(e) => !isExpanded && (e.currentTarget.style.backgroundColor = styles.secondaryBg)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        {displayColumns.map((columnKey) => (
          <td key={columnKey} className={`px-6 py-3 whitespace-nowrap ${getAlignmentClass(columnKey)}`}>
            {renderCell(columnKey)}
          </td>
        ))}
        <td className="px-6 py-3 whitespace-nowrap text-right">
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 inline" style={{ color: styles.textPrimary }} />
          ) : (
            <ChevronDown className="h-4 w-4 inline" style={{ color: styles.textSecondary }} />
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td
            colSpan={displayColumns.length + 1}
            style={{ borderBottom: `1px solid ${styles.borderColor}`, backgroundColor: 'transparent' }}
          >
            <div className="px-6 py-4">
              <TransactionDetailView transaction={transaction} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default TransactionRow;
